import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ToastStyled } from 'src/components';

type CopyToastOptions = {
  content?: ReactNode;
  variant?: 'success' | 'info';
  toastTitle?: string;
  duration?: number;
};

// Copies text to the clipboard and shows a ToastStyled confirmation.
// Callers control the payload and toast content; defaults to the standard
// list-link toast: info variant, generic title, "link copied" body.
export const useCopyToClipboard = () => {
  const { t } = useTranslation();

  return async (text: string, options: CopyToastOptions = {}) => {
    const { content, variant = 'info', toastTitle, duration } = options;

    try {
      await navigator.clipboard.writeText(text);
      toast.custom(
        (toastBlock) => (
          <ToastStyled
            variant={variant}
            toastTitle={toastTitle ?? t('popup.copied_to_clipboard')}
            handleClose={() => toast.dismiss(toastBlock)}
          >
            {content ?? t('popup.link_to_list_copied')}
          </ToastStyled>
        ),
        duration != null ? { duration } : undefined,
      );
    } catch {
      /* clipboard unavailable */
    }
  };
};
